import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CURRENCIES, type Currency } from "../entities/ledger/types";
import { AccountsSummary } from "../features/accounts/AccountsSummary";
import { AccountTile, distinctLabels } from "../features/accounts/AccountTile";
import { ClosedAccounts } from "../features/accounts/ClosedAccounts";
import { OpenAccountForm } from "../features/accounts/OpenAccountForm";
import { setReportingCurrency } from "../features/workspace/api";
import { useDashboard } from "../features/workspace/useDashboard";
import { humanError } from "../shared/api/client";
import { ChipGroup } from "../shared/ui/ChipGroup";
import { Modal } from "../shared/ui/Modal";
import { ErrorPanel, LoadingPanel } from "../shared/ui/PageState";
import { EmptyState } from "../shared/ui/EmptyState";

interface AccountsPageProps {
  currency: Currency;
  creating: boolean;
  onNotify: (message: string) => void;
}

export function AccountsPage({ currency, creating, onNotify }: AccountsPageProps) {
  const [reportingOpen, setReportingOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboard = useDashboard(currency);
  const reporting = useMutation({
    mutationFn: (value: Currency) => setReportingCurrency(dashboard.data!.ids.book, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onNotify("Reporting currency updated. History is unchanged; values are re-expressed on read.");
    },
  });

  if (!dashboard.data) {
    return (
      <section className="accounts-page view" aria-labelledby="accounts-page-title">
        <div className="page-heading"><div><p className="eyebrow">Accounts</p><h1 id="accounts-page-title">Every place your money lives</h1></div></div>
        {dashboard.isPending ? <LoadingPanel message="Loading your accounts…" /> : null}
        {dashboard.isError ? <ErrorPanel error={dashboard.error} onRetry={() => dashboard.refetch()} what="your accounts" /> : null}
      </section>
    );
  }
  const data = dashboard.data;
  const positions = new Map(data.balances.map((balance) => [balance.account.id, balance]));
  // Accounts are where money is spent from, whether that money is held or
  // borrowed; a credit card belongs here beside the current account. A debt
  // instrument — a loan, a mortgage — is not an account and gets its own
  // section. Accounting nature alone does not decide this.
  const spendable = data.accounts;
  const spendableLabels = distinctLabels(spendable);
  const reportingCurrency = data.report.reportingCurrency;
  const worth = data.netWorth;
  const assetCount = data.accounts.length;

  return (
    <section className="accounts-page view" aria-labelledby="accounts-page-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Accounts</p>
          <h1 id="accounts-page-title">Every place your money lives</h1>
          <p className="lede">Figures are calculated from each account's own history. What an account owes is shown as debt, never as a negative balance.</p>
        </div>
        {creating ? null : (
          <Link className="primary-button" to="/accounts/new"><span>Add account</span><span aria-hidden="true">＋</span></Link>
        )}
      </div>

      <AccountsSummary netWorth={worth} baseCurrency={data.ids.baseCurrency} reportingCurrency={reportingCurrency} accountCount={assetCount} />

      {creating ? (
        <section className="section-block accounts-panel create-panel" aria-labelledby="create-account-title">
          <div className="panel-heading"><div><p className="eyebrow">New account</p><h2 id="create-account-title">Add an account</h2></div></div>
          <OpenAccountForm
            bookID={data.ids.book}
            currency={data.ids.baseCurrency}
            onOpened={(account) => {
              onNotify(`${account.name} opened.`);
              navigate(`/accounts/${encodeURIComponent(account.id)}`, { replace: true });
            }}
            onCancel={() => navigate("/accounts")}
          />
        </section>
      ) : null}

      <div className="filter-summary">
        <span className="filter-facts">
          <span className="filter-fact"><small>Reporting currency</small><strong>{reportingCurrency}{reportingCurrency === data.ids.baseCurrency ? " (base)" : ""}</strong></span>
          <span className="filter-fact"><small>Base currency</small><strong>{data.ids.baseCurrency}</strong></span>
        </span>
        <button className="secondary-button" type="button" onClick={() => setReportingOpen(true)}>Change</button>
      </div>

      <Modal
        open={reportingOpen} onClose={() => setReportingOpen(false)} title="Reporting currency"
        subtitle="History never changes. The reporting currency only re-expresses derived figures, and only where a rate for the day exists."
        footer={<button className="secondary-button" type="button" onClick={() => setReportingOpen(false)}>Done</button>}
      >
        <ChipGroup
          label="Reporting currency" value={reportingCurrency}
          onChange={(value) => reporting.mutate(value as Currency)}
          options={CURRENCIES.map((value) => ({
            value, label: value === data.ids.baseCurrency ? `${value} (base)` : value, disabled: reporting.isPending,
          }))}
        />
        {reporting.error ? <p className="form-error" role="alert">{humanError(reporting.error)}</p> : null}
      </Modal>

      <section className="section-block accounts-panel" aria-labelledby="asset-accounts-title">
        <div className="panel-heading"><div><p className="eyebrow">Accounts</p><h2 id="asset-accounts-title">Where you spend from</h2></div></div>
        <div className="account-grid">
          {spendable.map((account) => <AccountTile account={account} balance={positions.get(account.id)} labelIsDistinct={spendableLabels.has(account.displayLabel ?? "")} key={account.id} />)}
          {spendable.length === 0 ? <EmptyState>No accounts yet — an account is where money sits, so the rest of the product has nothing to describe without one.</EmptyState> : null}
        </div>
      </section>

      <ClosedAccounts bookID={data.ids.book} onNotify={onNotify} />
    </section>
  );
}
